/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'motion/react';
import { sendPasswordResetEmail, ActionCodeSettings } from 'firebase/auth';
import { auth, isUsingMockKeys } from '../../firebase';
import { useWBL } from '../../context/WBLContext';
import { Mail, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import { GlobalFooter } from '../Common/GlobalFooter';

export const ForgotPasswordPage: React.FC = () => {
  const { globalBranding } = useWBL();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const primaryColor = globalBranding?.palette?.lightMain || '#0094c8';

  const navigateToLogin = () => {
    if (typeof window !== 'undefined') {
      if ((window as any).smoothNavigate) {
        (window as any).smoothNavigate('/login');
      } else {
        window.location.href = '/login';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    const actionCodeSettings: ActionCodeSettings = {
      url: 'https://thespartanburgedge.com/reset-password',
      handleCodeInApp: false,
    };

    try {
      if (isUsingMockKeys) {
        // Simulate network dispatch delay for developer mode
        await new Promise((resolve) => setTimeout(resolve, 800));
      } else {
        try {
          await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
        } catch (innerErr: any) {
          if (innerErr.code === 'auth/unauthorized-continue-uri') {
            console.warn(
              "WBL Portal: 'thespartanburgedge.com' is not yet listed in Firebase Authentication authorized domains. " +
              "Falling back to standard email dispatch without custom action settings."
            );
            // Fall back to sending standard reset link which uses standard Firebase template URL
            await sendPasswordResetEmail(auth, cleanEmail);
          } else {
            throw innerErr;
          }
        }
      }
      setIsSuccess(true);
    } catch (err: any) {
      console.error('Password reset email dispatch failed:', err);
      let msg = 'Failed to send password reset email. Please try again.';
      if (err.code === 'auth/invalid-email') {
        msg = 'The email address format is invalid.';
      } else if (err.code === 'auth/user-not-found') {
        // Display user-friendly message
        msg = 'No account was found with that email address. Please check the spelling or sign up.';
      } else if (err.code === 'auth/too-many-requests') {
        msg = 'Too many requests. Please wait a few minutes before trying again.';
      } else if (err.message) {
        msg = err.message;
      }
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between font-sans selection:bg-[#0094c8] selection:text-white relative overflow-x-hidden">
      {/* Background Decorative Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[400px] bg-gradient-to-b from-[#011d3c]/80 via-slate-950/40 to-transparent pointer-events-none -z-10 blur-3xl opacity-60" />

      {/* Header Bar */}
      <header className="border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-md sticky top-0 z-50 px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button 
            onClick={navigateToLogin}
            className="flex items-center space-x-3 cursor-pointer group text-left outline-none"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#011d3c] to-[#0094c8] p-0.5 shadow-lg shadow-[#0094c8]/20 group-hover:scale-105 transition-all">
              <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#0094c8]" />
              </div>
            </div>
            <div>
              <h1 className="text-base font-black tracking-tight text-white uppercase leading-none">
                The Spartanburg Edge
              </h1>
              <p className="text-[10px] font-bold text-slate-400 tracking-widest uppercase mt-0.5">
                Work-Based Learning Portal
              </p>
            </div>
          </button>

          <button
            onClick={navigateToLogin}
            className="flex items-center space-x-2 text-xs font-bold text-slate-400 hover:text-white transition-colors cursor-pointer px-3 py-1.5 rounded-lg border border-slate-800 hover:border-slate-700 bg-slate-900/50"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Return to Login</span>
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex items-center justify-center p-4 lg:p-8 my-auto z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-3xl shadow-2xl shadow-slate-950/80 overflow-hidden backdrop-blur-xl"
        >
          {/* Top Banner Header */}
          <div className="bg-[#011d3c] p-6 lg:p-8 text-white relative overflow-hidden border-b border-slate-800">
            <div className="absolute top-0 right-0 p-8 opacity-10 rotate-12 pointer-events-none">
              <ShieldCheck className="w-32 h-32 text-white" />
            </div>
            <div className="relative z-10 space-y-2">
              <div className="inline-flex items-center space-x-2 px-2.5 py-1 rounded-full bg-[#0094c8]/20 border border-[#0094c8]/30 text-[#0094c8] text-[10px] font-extrabold uppercase tracking-widest">
                <KeyRound className="w-3.5 h-3.5" />
                <span>Account Recovery</span>
              </div>
              <h2 className="text-2xl font-black uppercase tracking-tight text-white mt-1">
                Forgot Password?
              </h2>
              <p className="text-slate-300 text-xs font-medium leading-relaxed">
                Enter your account email address below. We'll send you a secure verification link to reset your password.
              </p>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6 lg:p-8">
            {isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6 py-2"
              >
                <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto text-emerald-400 shadow-lg shadow-emerald-500/10">
                  <CheckCircle2 className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    Reset Email Dispatched
                  </h3>
                  <p className="text-slate-300 text-xs leading-relaxed max-w-sm mx-auto">
                    We have sent a password reset link to:
                  </p>
                  <p className="text-sm font-bold text-[#0094c8] bg-slate-950 px-4 py-2.5 rounded-xl border border-slate-800 inline-block break-all font-mono">
                    {email}
                  </p>
                  <p className="text-[11px] text-slate-400 leading-relaxed pt-2">
                    Please check your inbox and follow the instructions in the email. If you don't see it within a few minutes, check your spam or junk folder.
                  </p>
                  <p className="text-[10px] text-slate-500 bg-slate-950/40 p-3 rounded-xl border border-slate-800/60 leading-relaxed mt-2 text-left">
                    <strong>Note:</strong> If you do not receive an email, please ensure that this email address is registered as an active account on the platform. For security reasons, the platform always reports a successful dispatch (email enumeration protection) regardless of whether the account exists.
                  </p>
                </div>

                <div className="pt-2 space-y-3">
                  <button
                    onClick={navigateToLogin}
                    className="w-full py-3.5 px-6 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg shadow-[#0094c8]/20 flex items-center justify-center space-x-2 cursor-pointer"
                  >
                    <span>Back to Sign In</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      setErrorMessage(null);
                    }}
                    className="w-full py-2.5 text-xs text-slate-400 hover:text-white font-medium transition-colors cursor-pointer"
                  >
                    Didn't receive the email? Try again
                  </button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-950/50 border border-red-800/80 rounded-xl flex items-start space-x-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-200 font-medium leading-relaxed">
                      {errorMessage}
                    </p>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-[11px] font-black uppercase tracking-wider text-slate-300">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full pl-11 pr-4 py-3.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#0094c8] focus:ring-1 focus:ring-[#0094c8] transition-all font-medium"
                    />
                    <Mail className="w-5 h-5 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-[10px] text-slate-400">
                    Use the email address associated with your Spartanburg Edge account.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full py-4 text-white rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      <span>Sending Instructions...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Reset Link</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <div className="pt-2 text-center border-t border-slate-800/80">
                  <button
                    type="button"
                    onClick={navigateToLogin}
                    className="inline-flex items-center space-x-2 text-xs font-bold text-slate-400 hover:text-white transition-colors cursor-pointer py-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Back to Login</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <GlobalFooter />
    </div>
  );
};
